import { getDafTrackingProperties } from "@/canvas/features/FormV2/hooks/useDafTracking";
import React, { useCallback, useContext, useMemo, useRef } from "react";

import {
  Emptyable,
  ensureAbsoluteUrl,
  FormType,
  PaymentMethod,
  FrontendRoutes,
  parseFormBackgroundTheme,
  TicketingFormCategory,
} from "@simplyk/common";
import { useElements } from "@stripe/react-stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import { UseFormHandleSubmit, UseFormSetValue } from "react-hook-form";

import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "../../../../contexts/FormV2Context";

import { unknownPaymentErrorCode, validateAndExtractAddress, validateFulfillmentCheckoutForSubmit } from "./helper";
import { usePaymentErrorContext } from "./PaymentErrorContext";
import { getDonationSelectionTracking } from "@/canvas/features/FormV2/helpers/getDonationSelectionTracking";
import { useAmplitudePaymentEventLog } from "./useAmplitudePaymentEventLog";
import { useCashapp } from "./useCashapp";
import { useOfflinePayment } from "./useOfflinePayment";
import { usePad } from "./usePad";
import { useStripePayment } from "./useStripePayment";
import { useStripePaymentReadiness } from "./useStripePaymentReadiness";

import { sendEmbedLoadingMessage } from "@/canvas/features/FormV2/helpers/dynamicEmbedMessaging";
import { getFormAddOnAnalytics } from "@/canvas/features/FormV2/helpers/splitCartQuantities";
import { getAnimationNumber, TIME_BETWEEN_STEPS } from "@/canvas/features/ThankYouLoader/helpers";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { SessionContext } from "@/common/contexts/SessionContext";
import { StripeContext } from "@/common/contexts/StripeContext";
import { pushGtmCommandSubmittedSuccessfully } from "@/common/helpers/gtm";
import { hasRequiredSepaMandateConsent } from "@/common/helpers/payment-method";
import { useMembershipRebuy } from "@/canvas/features/FormV2/hooks/useMembershipRebuy";
import {
  getNextPaymentDateParam,
  getThankYouPageUrl,
  hasCumulativeReceiptPayment,
  navigateAfterPayment,
} from "@/common/helpers/thankYouPage";
import { runWithMinimumDuration } from "@/common/helpers/time";
import { AppRouterType } from "@/common/helpers/trpc";
import { useDurationOnPage } from "@/common/hooks/useDurationOnPage";
import { useIsEmbeddedModal } from "@/common/hooks/useEmbeddedModal";
import { useFormQueryParams } from "@/common/hooks/useFormParams";
import { useRegistrationRedirect } from "@/common/hooks/useRegistrationRedirect";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import {
  DiscountOutput,
  DonationFormFrontendPageOutput,
  TicketingFrontendPageOutput,
  FormOrganizationOutput,
} from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export type Props = {
  handleSubmit: UseFormHandleSubmit<TicketingPaymentInput | DonationFormPaymentInput>;
  formObject: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  validDiscount?: Emptyable<DiscountOutput>;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function usePaymentSubmit({ handleSubmit, formObject, validDiscount, setValue }: Props) {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const { ticketingEvent, formObject: ticketing, addOnRates, ticketsPurchased } = useTicketingFormV2ContextOrDefaults();
  const { sessionId } = useContext(SessionContext);
  const { expressCheckoutWallet } = useContext(StripeContext);
  const { impactChainUtmSourceEmail, source } = useFormQueryParams();
  const {
    commandId,
    organization,
    formType,
    isArchived,
    isDraft,
    tip,
    selectedPaymentMethod,
    category,
    formData,
    postTransactionUrl,
    setIsSubmitting,
    paymentMode,
    selectedTip,
    shouldSendTipPercentage,
    isAuction,
    generateETicket,
    themeColor,
    setIsPadLoading,
    isEmbed,
    isEmbedV2,
    formEmbedVersion,
    embedId,
    displayedFormAmount,
    formAmplitudeContext,
    getTipChoice,
    displayAddressQuestion,
    currency,
  } = useContext(FrontendFormContext);
  const isEmbeddedModal = useIsEmbeddedModal(query);
  const { locale, isoLocale } = useLocaleContext();
  const { getDurationTimeOnPage } = useDurationOnPage();
  const { shouldRedirectToPeerToPeer, getRegistrationRedirectUrl } = useRegistrationRedirect();
  const elements = useElements();
  const context = useFormV2Context();
  const getMembershipRebuy = useMembershipRebuy();
  const { form } = context;
  const commandFulfillment = context.type === FormType.Ticketing ? context.commandFulfillment : null;
  const suggestedAmountsExperimentData =
    context.type === FormType.DonationForm ? context.suggestedAmountsExperimentData : null;
  const { itemsCount, addOnsCount } = getFormAddOnAnalytics(addOnRates, ticketsPurchased);
  const amplitudeEventLogModel = useMemo(
    () => ({
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing: { extraDonation: ticketing.extraDonation, thermometerTarget: ticketing.thermometerTarget },
      itemsCount,
      addOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      formObject,
      suggestedAmountsExperimentData,
      getDonationSelection: () => getDonationSelectionTracking(context),
    }),
    [
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing.extraDonation,
      ticketing.thermometerTarget,
      itemsCount,
      addOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet,
      formObject,
      suggestedAmountsExperimentData,
      context,
    ]
  );
  const { logDonorFormSubmitted } = useAmplitudePaymentEventLog({
    formStateErrors: form.formState.errors,
    formObject,
    model: amplitudeEventLogModel,
  });
  const { handlePaymentError, clearPaymentError } = usePaymentErrorContext();
  const ensureStripeReady = useStripePaymentReadiness(selectedPaymentMethod, handlePaymentError);
  const isSubmitInFlightRef = useRef(false);

  const getFormErrors = useCallback(async (): Promise<string | null> => {
    if (isArchived) {
      return t("common", "form_archived");
    }

    if (isDraft) {
      return t("common", "error.form_draft");
    }

    if (organization?.blockPayment) {
      return t("common", "payment_blocked");
    }

    if (selectedPaymentMethod !== PaymentMethod.Daf && !Boolean(organization?.isStripeCustomAccountActive)) {
      return t("common", "bankIsNotConnectedError");
    }

    return null;
  }, [
    isArchived,
    isDraft,
    organization?.blockPayment,
    organization?.isStripeCustomAccountActive,
    selectedPaymentMethod,
    t,
  ]);

  const getThankYouUrl = useCallback(
    ({
      command,
      paymentInput,
      formType,
      organization,
      emailUpdateToken,
      buyerTicketToken,
    }: {
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      formType: FormType;
      organization: FormOrganizationOutput;
      emailUpdateToken?: string;
      buyerTicketToken?: string;
    }): string => {
      return getThankYouPageUrl({
        command: command as NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"],
        formType,
        organization,
        postTransactionUrl,
        isEmbeddedModal,
        generateETicket,
        event: ticketingEvent,
        formData,
        isoLocale,
        category,
        isAuction,
        themeColor,
        isDarkMode: formObject.isDarkMode || false,
        backgroundTheme: parseFormBackgroundTheme(formObject.backgroundTheme),
        isEmbed,
        embedId: embedId ?? undefined,
        emailUpdateToken,
        buyerTicketToken,
        membershipRebuy: getMembershipRebuy(command, paymentInput),
        nextPaymentDate: getNextPaymentDateParam(paymentInput),
        hasCumulativeReceipt: hasCumulativeReceiptPayment(paymentInput),
      });
    },
    [
      postTransactionUrl,
      isEmbeddedModal,
      generateETicket,
      ticketingEvent,
      formData,
      isoLocale,
      category,
      isAuction,
      themeColor,
      formObject.isDarkMode,
      formObject.backgroundTheme,
      isEmbed,
      embedId,
      getMembershipRebuy,
    ]
  );

  const handlePostSubmit = useCallback(
    async ({
      paymentInput,
      command,
      transaction,
      formType,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    }: {
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      transaction: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["transaction"];
      formType: FormType;
      isExpressCheckout?: boolean;
      emailUpdateToken?: string;
      buyerTicketToken?: string;
    }): Promise<{ returnUrl?: string | null; error?: string | null }> => {
      logDonorFormSubmitted({
        isExpressCheckout: Boolean(isExpressCheckout),
        isPaymentSucceeded: true,
      });
      pushGtmCommandSubmittedSuccessfully({
        organization_id: organization?.id ?? "",
        form_id: formData.id,
        form_type: formType,
        form_category: category,
        amount: command?.productsAmount ? command.productsAmount / 100 : 0,
        extra_donation: command?.extraDonation ? command.extraDonation / 100 : 0,
        currency: command?.currency ?? undefined,
        transaction_id: transaction?.id ?? "",
      });

      // Redirect to P2P campaign page if this is a registration purchase
      if (shouldRedirectToPeerToPeer(paymentInput, formType)) {
        const redirectUrl = getRegistrationRedirectUrl({
          firstName: command?.firstName,
          lastName: command?.lastName,
          email: command?.email,
          commandId: command?.id,
        });
        if (redirectUrl) {
          return { returnUrl: redirectUrl };
        }
        return { error: "No associated peer-to-peer campaign found" };
      }
      const thankYouUrl = getThankYouUrl({
        command,
        paymentInput,
        formType,
        organization,
        emailUpdateToken,
        buyerTicketToken,
      });

      const returnUrl = ensureAbsoluteUrl(postTransactionUrl) || thankYouUrl;

      return { returnUrl };
    },
    [
      logDonorFormSubmitted,
      organization,
      formData,
      category,
      shouldRedirectToPeerToPeer,
      getThankYouUrl,
      postTransactionUrl,
      getRegistrationRedirectUrl,
    ]
  );

  const { payByPad } = usePad({ setValue, handlePaymentError, handlePostSubmit });
  const { payByCashapp } = useCashapp({ setValue, handlePaymentError, getThankYouUrl, handlePostSubmit });
  const { payOffline } = useOfflinePayment({
    setValue,
    handlePaymentError,
    handlePostSubmit,
    onDafSubmitted: (providerId) =>
      logDonorFormSubmitted({
        isExpressCheckout: false,
        isPaymentSucceeded: true,
        extra: getDafTrackingProperties(providerId),
      }),
  });
  const { payByStripe } = useStripePayment({ setValue, handlePaymentError, handlePostSubmit });

  const onInvalid = useCallback(
    async (isExpressCheckout: boolean) => {
      const addressElement = elements?.getElement?.("address");
      await addressElement?.getValue?.();
      // PayPal/Venmo are not handled by Stripe Elements — skip submit() for them
      if (
        selectedPaymentMethod &&
        selectedPaymentMethod !== PaymentMethod.PayPal &&
        selectedPaymentMethod !== PaymentMethod.Venmo &&
        selectedPaymentMethod !== PaymentMethod.Daf
      ) {
        await elements?.submit();
      }

      logDonorFormSubmitted({ isExpressCheckout, isPaymentSucceeded: false });
    },
    [elements, selectedPaymentMethod, logDonorFormSubmitted]
  );

  const handleFormSubmit = useCallback(
    (isExpressCheckout = false) =>
      async (event?: React.SyntheticEvent): Promise<boolean> => {
        event && event.preventDefault();

        if (isSubmitInFlightRef.current) {
          return false;
        }
        if (!ensureStripeReady(isExpressCheckout)) {
          return false;
        }
        isSubmitInFlightRef.current = true;

        // New payment attempt: clear the banner so a repeated identical server message still
        // updates `paymentError` (null → string) and PaymentErrorAlert’s scroll effect re-runs.
        clearPaymentError();

        let paymentSucceeded = false;

        await handleSubmit(
          async (paymentInput) => {
            if (!selectedPaymentMethod) {
              return;
            }

            if (
              selectedPaymentMethod === PaymentMethod.Daf &&
              !("dafProviderId" in paymentInput && paymentInput.dafProviderId)
            ) {
              if (context.type === FormType.DonationForm) {
                context.form.setError("dafProviderId", {
                  type: "required",
                  message: t("common", "daf.selectProvider"),
                });
              }
              logDonorFormSubmitted({
                isExpressCheckout,
                isPaymentSucceeded: false,
                errorCode: "daf_provider_required",
              });
              return;
            }

            if (!hasRequiredSepaMandateConsent(selectedPaymentMethod, paymentInput.sepaMandateAccepted)) {
              form.setError(
                "sepaMandateAccepted",
                {
                  type: "required",
                  message: t("common", "payment.sepa.consentRequired"),
                },
                { shouldFocus: true }
              );
              return;
            }

            if (selectedPaymentMethod !== PaymentMethod.Pad) {
              setIsSubmitting(true);
            } else if (selectedPaymentMethod === PaymentMethod.Pad) {
              setIsPadLoading(true);
            }

            const minimumDuration =
              selectedPaymentMethod !== PaymentMethod.CashApp && selectedPaymentMethod !== PaymentMethod.Daf
                ? TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: false })
                : 0;
            const url = await runWithMinimumDuration<Emptyable<string>>(async () => {
              const formError = await getFormErrors();
              if (formError) {
                handlePaymentError({
                  rawErrorCode: "form_validation_error",
                  rawErrorMessage: formError,
                  step: "form-validation",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  isKnownError: true,
                });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              const addressValidationResult = await validateAndExtractAddress({
                elements,
                paymentInput,
                t,
                handlePaymentError,
                selectedPaymentMethod,
                isExpressCheckout,
                commandId,
                setValue,
              });

              if (addressValidationResult.shouldEarlyReturn) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if (context.type === FormType.Ticketing && context.formCategory === TicketingFormCategory.Lottery) {
                context.form.setValue("country", paymentInput.country);
                context.form.setValue("region", paymentInput.region);
                if (!(await context.form.trigger("raffleEligibility"))) {
                  return { callbackResult: null, shouldAwaitMinimumDuration: false };
                }
              }

              if (
                validateFulfillmentCheckoutForSubmit({
                  paymentInput,
                  formType,
                  t,
                  handlePaymentError,
                  selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  requireFulfillmentCheckout: commandFulfillment?.showFulfillmentOnPayment ?? false,
                  canOfferSameAsBilling: displayAddressQuestion,
                  offeredMethods: commandFulfillment?.offeredMethods ?? [],
                })
              ) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if ((tip && tip < 0) || !organization?.country || !formType) {
                handlePaymentError({
                  rawErrorCode: "invalid_form_state",
                  rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.other2.code }),
                  step: "form-validation",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  commandId,
                  isKnownError: false,
                });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              const submitCommandInput: SubmitCommandFormInput = {
                paymentInput,
                paymentMethod: selectedPaymentMethod,
                formObject,
                user: null,
                contact: null,
                formType,
                validDiscount: validDiscount || null,
                session: { sessionId, durationOnPage: getDurationTimeOnPage() },
                routerQuery: query,
                commandId,
                locale,
                tip: (!shouldSendTipPercentage ? tip : 0) || 0,
                tipPercentage: (shouldSendTipPercentage ? selectedTip.percentage : 0) || 0,
                stripePaymentMethodId: null,
                isExpressCheckout,
                formEmbedVersion: formEmbedVersion ?? undefined,
              };

              if (selectedPaymentMethod === PaymentMethod.Pad) {
                return {
                  callbackResult: await payByPad(submitCommandInput, paymentMode, minimumDuration),
                  shouldAwaitMinimumDuration: false,
                };
              }
              if (selectedPaymentMethod === PaymentMethod.CashApp) {
                return {
                  callbackResult: await payByCashapp(submitCommandInput, paymentMode),
                  shouldAwaitMinimumDuration: false,
                };
              }

              if (
                selectedPaymentMethod === PaymentMethod.Cheque ||
                selectedPaymentMethod === PaymentMethod.Cash ||
                selectedPaymentMethod === PaymentMethod.Daf
              ) {
                return {
                  callbackResult: await payOffline(submitCommandInput),
                  shouldAwaitMinimumDuration: selectedPaymentMethod !== PaymentMethod.Daf,
                };
              }

              if (selectedPaymentMethod === PaymentMethod.PayPal || selectedPaymentMethod === PaymentMethod.Venmo) {
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              return { callbackResult: await payByStripe(submitCommandInput), shouldAwaitMinimumDuration: true };
            }, minimumDuration);
            if (url) {
              paymentSucceeded = true;
              if (isEmbedV2 && url.includes(FrontendRoutes.EmbedThankYou)) {
                sendEmbedLoadingMessage();
              }
              try {
                await navigateAfterPayment(url);
              } catch (redirectError) {
                handlePaymentError({
                  rawErrorCode: "thank_you_redirect_failed",
                  rawErrorMessage: redirectError instanceof Error ? redirectError.message : String(redirectError),
                  step: "post-submit",
                  paymentMethod: selectedPaymentMethod,
                  isExpressCheckout,
                  isPaymentSucceeded: true,
                  isKnownError: false,
                });
              }
            }
          },
          () => onInvalid(isExpressCheckout)
        )().finally(() => {
          isSubmitInFlightRef.current = false;
          setIsSubmitting(false);
          setIsPadLoading(false);
        });

        return paymentSucceeded;
      },
    [
      ensureStripeReady,
      handleSubmit,
      context,
      logDonorFormSubmitted,
      form,
      selectedPaymentMethod,
      setIsSubmitting,
      setIsPadLoading,
      getFormErrors,
      elements,
      tip,
      organization?.country,
      formType,
      formObject,
      validDiscount,
      sessionId,
      getDurationTimeOnPage,
      query,
      commandId,
      locale,
      shouldSendTipPercentage,
      selectedTip.percentage,
      payByStripe,
      handlePaymentError,
      t,
      setValue,
      payByPad,
      paymentMode,
      payByCashapp,
      payOffline,
      onInvalid,
      isEmbedV2,
      formEmbedVersion,
      clearPaymentError,
      commandFulfillment?.showFulfillmentOnPayment,
      commandFulfillment?.offeredMethods,
      displayAddressQuestion,
    ]
  );

  const prepareSubmit = useCallback(
    async (paymentMethod: PaymentMethod): Promise<SubmitCommandFormInput | null> => {
      const isValid = await form.trigger(undefined, { shouldFocus: true });
      if (!isValid) {
        return null;
      }

      const paymentInput = form.getValues() as DonationFormPaymentInput | TicketingPaymentInput;

      const formError = await getFormErrors();
      if (formError) {
        handlePaymentError({
          rawErrorCode: "form_validation_error",
          rawErrorMessage: formError,
          step: "form-validation",
          paymentMethod,
          isExpressCheckout: false,
          commandId,
          isKnownError: true,
        });
        return null;
      }

      const addressValidationResult = await validateAndExtractAddress({
        elements,
        paymentInput,
        t,
        handlePaymentError,
        selectedPaymentMethod: paymentMethod,
        isExpressCheckout: false,
        commandId,
        setValue,
      });

      if (addressValidationResult.shouldEarlyReturn) {
        return null;
      }

      if (context.type === FormType.Ticketing && context.formCategory === TicketingFormCategory.Lottery) {
        context.form.setValue("country", paymentInput.country);
        context.form.setValue("region", paymentInput.region);
        if (!(await context.form.trigger("raffleEligibility"))) {
          return null;
        }
      }

      if (
        formType &&
        validateFulfillmentCheckoutForSubmit({
          paymentInput,
          formType,
          t,
          handlePaymentError,
          selectedPaymentMethod: paymentMethod,
          isExpressCheckout: false,
          commandId,
          requireFulfillmentCheckout: commandFulfillment?.showFulfillmentOnPayment ?? false,
          canOfferSameAsBilling: displayAddressQuestion,
          offeredMethods: commandFulfillment?.offeredMethods ?? [],
        })
      ) {
        return null;
      }

      if ((tip && tip < 0) || !organization?.country || !formType) {
        handlePaymentError({
          rawErrorCode: "invalid_form_state",
          rawErrorMessage: t("common", "unknown_error", { code: unknownPaymentErrorCode.other2.code }),
          step: "form-validation",
          paymentMethod,
          isExpressCheckout: false,
          commandId,
          isKnownError: false,
        });
        return null;
      }

      return {
        paymentInput,
        paymentMethod,
        formObject,
        user: null,
        contact: null,
        formType,
        validDiscount: validDiscount || null,
        session: { sessionId, durationOnPage: getDurationTimeOnPage() },
        routerQuery: query,
        commandId,
        locale,
        tip: (!shouldSendTipPercentage ? tip : 0) || 0,
        tipPercentage: (shouldSendTipPercentage ? selectedTip.percentage : 0) || 0,
        stripePaymentMethodId: null,
        isExpressCheckout: false,
        formEmbedVersion: formEmbedVersion ?? undefined,
      };
    },
    [
      form,
      getFormErrors,
      handlePaymentError,
      commandId,
      elements,
      t,
      tip,
      organization?.country,
      formType,
      formObject,
      validDiscount,
      sessionId,
      getDurationTimeOnPage,
      query,
      locale,
      shouldSendTipPercentage,
      selectedTip.percentage,
      formEmbedVersion,
      setValue,
      commandFulfillment?.showFulfillmentOnPayment,
      commandFulfillment?.offeredMethods,
      displayAddressQuestion,
      context,
    ]
  );

  return { handleFormSubmit, prepareSubmit, handlePostSubmit };
}
